(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-cart-drawer';
  var BODY_LOCK_CLASS = 'oddit-cart-drawer-open';

  var sectionRegistry = new WeakMap();
  var activeCurrency = null;
  var latestCart = null;

  // The actual Shopify section.id (e.g. `sections--<hash>__oddit_cart_drawer`
  // when rendered inside a section group) is rendered into `data-section-id`
  // by oddit-cart-drawer.liquid. We collect those IDs to drive the Section
  // Rendering API — hardcoding the section file name does not work for
  // sections registered in section groups or JSON templates.
  function getSectionIds() {
    return qsa(document, SECTION_SELECTOR)
      .map(function (section) {
        return section.getAttribute('data-section-id');
      })
      .filter(Boolean);
  }

  function getSectionsParam() {
    return getSectionIds().join(',');
  }

  // -------------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------------

  function getActiveCurrency() {
    if (activeCurrency) return activeCurrency;
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return window.Shopify.currency.active;
    }
    return 'USD';
  }

  function formatMoney(cents) {
    var currency = getActiveCurrency();
    var amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2) + ' ' + currency;
    }
  }

  function escapeHTML(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function escapeAttr(str) {
    return escapeHTML(str);
  }

  function qsa(root, selector) {
    if (!root) return [];
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function qs(root, selector) {
    return root ? root.querySelector(selector) : null;
  }

  function getFocusable(root) {
    if (!root) return [];
    var selector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');
    return qsa(root, selector).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  // -------------------------------------------------------------------------
  // Cart API methods
  // -------------------------------------------------------------------------

  function fetchJSON(url, options) {
    options = options || {};
    options.headers = Object.assign(
      {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      options.headers || {}
    );
    return fetch(url, options).then(function (res) {
      if (!res.ok) {
        return res
          .json()
          .catch(function () {
            return { description: res.statusText, status: res.status };
          })
          .then(function (err) {
            var error = new Error(err.description || err.message || 'Cart request failed');
            error.status = res.status;
            error.data = err;
            throw error;
          });
      }
      return res.json();
    });
  }

  function getCart() {
    return fetchJSON('/cart.js', { method: 'GET' }).then(function (cart) {
      renderCart(cart);
      return cart;
    });
  }

  function refreshCart() {
    return getCart();
  }

  function addToCart(variantId, quantity, properties) {
    quantity = quantity == null ? 1 : Number(quantity);
    var body = {
      id: Number(variantId),
      quantity: quantity,
      sections: getSectionsParam()
    };
    if (properties && Object.keys(properties).length > 0) {
      body.properties = properties;
    }
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify(body)
    }).then(function (response) {
      applyCart(response); // swaps panel HTML from response.sections
      openAll();
      return getCart();
    });
  }

  function addMultipleToCart(items) {
    items = (items || []).map(function (item) {
      var entry = {
        id: Number(item.id),
        quantity: item.quantity == null ? 1 : Number(item.quantity)
      };
      if (item.properties && Object.keys(item.properties).length > 0) {
        entry.properties = item.properties;
      }
      if (item.selling_plan) entry.selling_plan = item.selling_plan;
      return entry;
    });
    return fetchJSON('/cart/add.js', {
      method: 'POST',
      body: JSON.stringify({ items: items, sections: getSectionsParam() })
    }).then(function (response) {
      applyCart(response); // swaps panel HTML from response.sections
      openAll();
      // add.js returns line items, not the cart — fetch it so cart:updated
      // carries the real item_count (QA: ALY-45).
      return getCart();
    });
  }

  function updateLineItemQuantity(lineItemKey, quantity) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({ id: lineItemKey, quantity: Number(quantity), sections: getSectionsParam() })
    }).then(function (cart) {
      applyCart(cart);
      return cart;
    });
  }

  function removeFromCart(lineItemKey) {
    return updateLineItemQuantity(lineItemKey, 0);
  }

  // Applies the full set of discount codes to the cart. Shopify's
  // /cart/update.js accepts a comma-separated `discount` string and returns the
  // cart (including `discount_codes` with per-code `applicable` flags) plus the
  // re-rendered section HTML requested via `sections`.
  function applyDiscountCodes(codes) {
    return fetchJSON('/cart/update.js', {
      method: 'POST',
      body: JSON.stringify({ discount: codes.join(','), sections: getSectionsParam() })
    });
  }

  function updateLineItemToSubscription(lineItemKey, sellingPlanId) {
    return fetchJSON('/cart/change.js', {
      method: 'POST',
      body: JSON.stringify({
        id: lineItemKey,
        selling_plan: sellingPlanId == null || sellingPlanId === '' ? null : sellingPlanId,
        sections: getSectionsParam()
      })
    }).then(function (cart) {
      applyCart(cart);
      return cart;
    });
  }

  // -------------------------------------------------------------------------
  // Rendering — Section Rendering API
  //
  // All cart mutation endpoints request `sections=oddit_cart_drawer` and return
  // freshly-rendered Liquid HTML in `cart.sections.oddit_cart_drawer`. We swap
  // the inner `.oddit-cart-drawer__panel` so the outer drawer (overlay, open
  // state) is preserved. Event handlers on inner elements must use delegation.
  // -------------------------------------------------------------------------

  function applyCart(cart) {
    if (!cart) return;

    if (cart.currency) {
      activeCurrency = cart.currency;
    }

    if (cart.sections) {
      qsa(document, SECTION_SELECTOR).forEach(function (section) {
        var sectionId = section.getAttribute('data-section-id');
        var sectionHTML = sectionId && cart.sections[sectionId];
        if (!sectionHTML) return;
        var doc = new DOMParser().parseFromString(sectionHTML, 'text/html');
        var newPanel = doc.querySelector('.oddit-cart-drawer__panel');
        if (!newPanel) return;
        var oldPanel = section.querySelector('.oddit-cart-drawer__panel');
        if (oldPanel) {
          oldPanel.replaceWith(newPanel);
        }
      });
    }

    // /cart/add.js responds with the added line item(s), not the cart object —
    // only objects with item_count are cart state. Broadcasting a non-cart here
    // blanked the "[data-cart-count]" badges (QA: ALY-45).
    if (typeof cart.item_count === 'undefined') return;

    latestCart = cart;

    // Header cart count and any other listeners outside the cart drawer.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  // Back-compat alias for any external callers that called window.OdditCartDrawer.renderCart.
  var renderCart = applyCart;

  // -------------------------------------------------------------------------
  // Drawer UI
  // -------------------------------------------------------------------------

  function openAll() {
    qsa(document, SECTION_SELECTOR).forEach(function (section) {
      openDrawer(section);
    });
  }

  function closeAll() {
    qsa(document, SECTION_SELECTOR).forEach(function (section) {
      closeDrawer(section);
    });
  }

  function toggleAll() {
    qsa(document, SECTION_SELECTOR).forEach(function (section) {
      if (section.classList.contains('is-open')) closeDrawer(section);
      else openDrawer(section);
    });
  }

  function openDrawer(section) {
    if (!section) return;
    var state = sectionRegistry.get(section);
    if (!state) return;

    state.previouslyFocused = document.activeElement;
    section.style.display = '';
    // Force reflow so transitions apply.
    // eslint-disable-next-line no-unused-expressions
    section.offsetHeight;
    section.classList.add('is-open');
    section.setAttribute('aria-hidden', 'false');
    document.body.classList.add(BODY_LOCK_CLASS);
    document.body.style.overflow = 'hidden';

    var focusables = getFocusable(section);
    var closeBtn = qs(section, '[data-cart-drawer-close]:not(.oddit-cart-drawer__overlay)');
    var firstFocus = closeBtn || focusables[0];
    if (firstFocus) {
      window.setTimeout(function () {
        firstFocus.focus();
      }, 50);
    }
  }

  function closeDrawer(section) {
    if (!section) return;
    var state = sectionRegistry.get(section);
    section.classList.remove('is-open');
    section.setAttribute('aria-hidden', 'true');
    document.body.classList.remove(BODY_LOCK_CLASS);
    document.body.style.overflow = '';

    if (state && state.previouslyFocused && typeof state.previouslyFocused.focus === 'function') {
      try {
        state.previouslyFocused.focus();
      } catch (e) {
        /* noop */
      }
      state.previouslyFocused = null;
    }
  }

  function setState(section, name) {
    if (!section) return;
    qsa(section, '[data-cart-state]').forEach(function (el) {
      if (el.getAttribute('data-cart-state') === name) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  function handleFocusTrap(section, event) {
    if (event.key !== 'Tab') return;
    var focusables = getFocusable(section);
    if (focusables.length === 0) {
      event.preventDefault();
      return;
    }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // -------------------------------------------------------------------------
  // Event wiring
  // -------------------------------------------------------------------------

  function bindCartItemEvents(section, signal) {
    // Delegated on `section` (not `[data-cart-items]`) because the panel —
    // and the items container with it — is replaced wholesale by the Section
    // Rendering API on every cart mutation. A listener bound inside the panel
    // would survive exactly one action.
    section.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !(target instanceof Element)) return;

        var inc = target.closest('[data-quantity-increment]');
        if (inc && section.contains(inc)) {
          event.preventDefault();
          var incKey = inc.getAttribute('data-line-item-key');
          var incValue = qs(section, '[data-quantity-value][data-line-item-key="' + incKey + '"]');
          var incQty = parseInt((incValue && incValue.textContent) || '1', 10) || 1;
          var incItem = inc.closest('[data-line-item]');
          if (incItem) incItem.classList.add('is-pending');
          inc.disabled = true;
          updateLineItemQuantity(incKey, incQty + 1)
            .catch(function (err) {
              console.error('[oddit-cart-drawer] increment failed:', err);
              if (incItem) incItem.classList.remove('is-pending');
            })
            .finally(function () {
              inc.disabled = false;
            });
          return;
        }

        var dec = target.closest('[data-quantity-decrement]');
        if (dec && section.contains(dec)) {
          event.preventDefault();
          var decKey = dec.getAttribute('data-line-item-key');
          var decValue = qs(section, '[data-quantity-value][data-line-item-key="' + decKey + '"]');
          var decQty = parseInt((decValue && decValue.textContent) || '1', 10) || 1;
          var decItem = dec.closest('[data-line-item]');
          if (decItem) decItem.classList.add('is-pending');
          dec.disabled = true;
          updateLineItemQuantity(decKey, Math.max(0, decQty - 1))
            .catch(function (err) {
              console.error('[oddit-cart-drawer] decrement failed:', err);
              if (decItem) decItem.classList.remove('is-pending');
            })
            .finally(function () {
              dec.disabled = false;
            });
          return;
        }

        var removeBtn = target.closest('[data-remove-item]');
        if (removeBtn && section.contains(removeBtn)) {
          event.preventDefault();
          var rmKey = removeBtn.getAttribute('data-line-item-key');
          var rmItem = removeBtn.closest('[data-line-item]');
          if (rmItem) rmItem.classList.add('is-pending');
          removeBtn.disabled = true;
          removeFromCart(rmKey)
            .catch(function (err) {
              console.error('[oddit-cart-drawer] remove failed:', err);
              if (rmItem) rmItem.classList.remove('is-pending');
            })
            .finally(function () {
              removeBtn.disabled = false;
            });
        }
      },
      { signal: signal }
    );
  }

  function bindUpsellButtons(section, signal) {
    // Delegated so handlers survive panel HTML swaps from the Section Rendering API.
    section.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !(target instanceof Element)) return;

        // Subscription switcher CTA.
        var subSwitch = target.closest('[data-sub-switch-cta]');
        if (subSwitch && section.contains(subSwitch)) {
          event.preventDefault();
          var raw = subSwitch.getAttribute('data-sub-switch-changes');
          if (!raw) return;
          var pairs;
          try {
            pairs = JSON.parse(raw);
          } catch (err) {
            console.error('[oddit-cart-drawer] sub-switch: invalid changes JSON', err, raw);
            return;
          }
          if (!Array.isArray(pairs) || pairs.length === 0) return;

          subSwitch.disabled = true;
          var chain = Promise.resolve(null);
          pairs.forEach(function (p) {
            if (!p || !p.key || p.plan == null) return;
            chain = chain.then(function () {
              return fetchJSON('/cart/change.js', {
                method: 'POST',
                body: JSON.stringify({ id: p.key, selling_plan: p.plan, sections: getSectionsParam() })
              });
            });
          });
          chain
            .then(function (cart) {
              if (cart) applyCart(cart);
            })
            .catch(function (err) {
              console.error('[oddit-cart-drawer] sub-switch failed:', err);
            });
          return;
        }

        // Cross-sell add-to-cart button (kept for future use).
        var recAdd = target.closest('.oddit-cart-drawer__rec-add[data-variant-id]');
        if (recAdd && section.contains(recAdd)) {
          event.preventDefault();
          var variantId = recAdd.getAttribute('data-variant-id');
          if (!variantId) return;
          recAdd.disabled = true;
          addToCart(Number(variantId))
            .catch(function (err) {
              console.error('[oddit-cart-drawer] rec add failed:', err);
            })
            .finally(function () {
              recAdd.disabled = false;
            });
        }
      },
      { signal: signal }
    );
  }

  // -------------------------------------------------------------------------
  // Discount code
  // -------------------------------------------------------------------------

  function getAppliedDiscountCodes(section) {
    return qsa(section, '[data-discount-pill][data-discount-code]')
      .map(function (el) {
        return el.getAttribute('data-discount-code');
      })
      .filter(Boolean);
  }

  function showDiscountError(section, code) {
    var err = qs(section, '[data-discount-error]');
    if (!err) return;
    var template = err.getAttribute('data-error-template');
    if (template) {
      err.textContent = template.replace('[code]', code);
    }
    err.hidden = false;
  }

  function bindDiscountForm(section, signal) {
    // Delegated on `section` because the panel — and the discount form with it —
    // is replaced wholesale by the Section Rendering API on every cart mutation.
    section.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (!form || !(form instanceof Element) || !form.matches('[data-discount-form]')) return;
        event.preventDefault();
        if (!section.contains(form)) return;

        var input = qs(form, '[data-discount-input]');
        if (!input) return;
        var code = (input.value || '').trim();
        if (!code) return;

        var existing = getAppliedDiscountCodes(section);
        var alreadyApplied = existing.some(function (c) {
          return c.toLowerCase() === code.toLowerCase();
        });
        if (alreadyApplied) {
          input.value = '';
          return;
        }

        var applyBtn = qs(form, '[data-discount-apply]');
        if (applyBtn) applyBtn.disabled = true;
        form.classList.add('is-loading');

        applyDiscountCodes(existing.concat([code]))
          .then(function (cart) {
            var invalid = (cart.discount_codes || []).some(function (d) {
              return d && d.code && d.code.toLowerCase() === code.toLowerCase() && d.applicable === false;
            });
            applyCart(cart);
            // applyCart swaps the panel, so re-query the (new) error element.
            if (invalid) showDiscountError(section, code);
          })
          .catch(function (err) {
            console.error('[oddit-cart-drawer] apply discount failed:', err);
            showDiscountError(section, code);
            if (applyBtn) applyBtn.disabled = false;
            form.classList.remove('is-loading');
          });
      },
      { signal: signal }
    );

    section.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !(target instanceof Element)) return;
        var removeBtn = target.closest('[data-discount-remove]');
        if (!removeBtn || !section.contains(removeBtn)) return;
        event.preventDefault();

        var code = removeBtn.getAttribute('data-discount-remove');
        var remaining = getAppliedDiscountCodes(section).filter(function (c) {
          return c !== code;
        });
        removeBtn.disabled = true;
        applyDiscountCodes(remaining)
          .then(applyCart)
          .catch(function (err) {
            console.error('[oddit-cart-drawer] remove discount failed:', err);
            removeBtn.disabled = false;
          });
      },
      { signal: signal }
    );
  }

  function bindCheckoutButtons(section, signal) {
    // Delegated for the same reason as the cart-item handler — the checkout
    // CTA lives inside the panel, which is replaced on every cart mutation.
    section.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !(target instanceof Element)) return;
        var btn = target.closest('.oddit-cart-drawer__checkout-cta, [data-checkout]');
        if (!btn || !section.contains(btn)) return;
        // Show loading state — the page navigates to /checkout next, so we
        // never need to remove the class.
        btn.classList.add('is-loading');
        btn.setAttribute('aria-busy', 'true');
        // Submit buttons inside <form action="/cart"> post naturally — Shopify
        // redirects to /checkout. Other elements need a manual navigation.
        if (btn.tagName === 'BUTTON' && btn.type === 'submit') return;
        event.preventDefault();
        window.location.href = '/checkout';
      },
      { signal: signal }
    );
  }

  // -------------------------------------------------------------------------
  // Init / teardown
  // -------------------------------------------------------------------------

  function initSection(section) {
    if (!section || section.dataset.odditInit === '1') return;
    section.dataset.odditInit = '1';

    var controller = new AbortController();
    var signal = controller.signal;

    var state = {
      controller: controller,
      previouslyFocused: null
    };
    sectionRegistry.set(section, state);

    // Close buttons (header close + overlay) — delegated so the in-panel
    // close button keeps working after Section Rendering API swaps.
    section.addEventListener(
      'click',
      function (event) {
        var target = event.target;
        if (!target || !(target instanceof Element)) return;
        var closeBtn = target.closest('[data-cart-drawer-close]');
        if (closeBtn && section.contains(closeBtn)) {
          event.preventDefault();
          closeDrawer(section);
        }
      },
      { signal: signal }
    );

    // Escape key + focus trap.
    section.addEventListener(
      'keydown',
      function (event) {
        if (event.key === 'Escape' && section.classList.contains('is-open')) {
          event.preventDefault();
          closeDrawer(section);
          return;
        }
        if (section.classList.contains('is-open')) {
          handleFocusTrap(section, event);
        }
      },
      { signal: signal }
    );

    // Cart item interactions (delegated).
    bindCartItemEvents(section, signal);

    // Upsell / cross-sell / subscription switch buttons.
    bindUpsellButtons(section, signal);

    // Discount code apply / remove.
    bindDiscountForm(section, signal);

    // Checkout button.
    bindCheckoutButtons(section, signal);

    // External open triggers (e.g. header cart icon) — listen for custom event.
    document.addEventListener(
      'cart:open',
      function () {
        openDrawer(section);
      },
      { signal: signal }
    );
    document.addEventListener(
      'cart:close',
      function () {
        closeDrawer(section);
      },
      { signal: signal }
    );
    document.addEventListener(
      'cart:toggle',
      function () {
        if (section.classList.contains('is-open')) closeDrawer(section);
        else openDrawer(section);
      },
      { signal: signal }
    );

    // Hydrate from current cart state on load so the drawer is always in sync.
    getCart().catch(function (err) {
      console.error('[oddit-cart-drawer] initial cart fetch failed:', err);
    });
  }

  function destroySection(section) {
    if (!section) return;
    var state = sectionRegistry.get(section);
    if (state && state.controller) {
      state.controller.abort();
    }
    sectionRegistry.delete(section);
    section.dataset.odditInit = '';
  }

  function initAll() {
    qsa(document, SECTION_SELECTOR).forEach(initSection);
  }

  // -------------------------------------------------------------------------
  // Boot
  // -------------------------------------------------------------------------

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor compatibility.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target && event.target.querySelector
      ? event.target.querySelector(SECTION_SELECTOR)
      : null;
    if (section) initSection(section);
  });
  document.addEventListener('shopify:section:unload', function (event) {
    var section = event.target && event.target.querySelector
      ? event.target.querySelector(SECTION_SELECTOR)
      : null;
    if (section) destroySection(section);
  });

  // -------------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------------

  window.OdditCartDrawer = {
    open: openAll,
    close: closeAll,
    toggle: toggleAll,
    setState: function (name) {
      qsa(document, SECTION_SELECTOR).forEach(function (section) {
        setState(section, name);
      });
    },
    addToCart: addToCart,
    addMultipleToCart: addMultipleToCart,
    removeFromCart: removeFromCart,
    updateLineItemQuantity: updateLineItemQuantity,
    updateLineItemToSubscription: updateLineItemToSubscription,
    getCart: getCart,
    refreshCart: refreshCart,
    renderCart: renderCart,
    formatMoney: formatMoney
  };
})();